import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

export interface SelectDropdownOption {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  tooltip?: string;
}

interface SelectDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectDropdownOption[];
  ariaLabel?: string;
  className?: string;
}

export function SelectDropdown({
  value,
  onChange,
  options,
  ariaLabel,
  className
}: SelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between gap-1.5 rounded-md border border-border/40 bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          className
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <div className="flex items-center gap-1.5">
          {selectedOption?.icon && (
            <img
              src={selectedOption.icon}
              alt={selectedOption.value}
              width={20}
              height={14}
              className="h-3.5 w-5 rounded object-cover border border-border/20"
            />
          )}
          <span className="text-xs font-semibold uppercase">{selectedOption?.label || value}</span>
        </div>
        <ChevronDown className="size-4 opacity-50 shrink-0" />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-1.5 z-50 w-full min-w-[95px] bg-popover text-popover-foreground rounded-md border border-border/80 p-1 shadow-md origin-top-right focus:outline-none animate-in fade-in-0 zoom-in-95 duration-100"
          role="listbox"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              title={opt.tooltip}
              onClick={() => {
                if (!opt.disabled) {
                  onChange(opt.value);
                  setIsOpen(false);
                }
              }}
              className={cn(
                "flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm outline-none select-none transition-colors",
                opt.disabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              )}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.icon && (
                <img
                  src={opt.icon}
                  alt={opt.value}
                  width={20}
                  height={14}
                  className="h-3.5 w-5 rounded object-cover border border-border/20 shrink-0"
                />
              )}
              <span className="text-xs font-semibold uppercase">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
